import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '../../assets/images/icons/icon-edit.svg';
import YesIcon from '../../assets/images/icons/icon-yes.svg';
import NoIcon from '../../assets/images/icons/icon-no.svg';

import moment from "moment";

import BlockContainer from "../../components/Container/BlockContainer";
import MainContainer from "../../components/Container/MainContainer";
import BaseImage from "../../components/BaseImage";
import ConciergeItem from "../../components/ConciergeItem"
import MedicationSortDropdown from "../../components/MedicationSortDropdown"
import MedicationSortMobileNavbar from "../../components/MedicationSortMobileNavbar"
import BaseLink from "../../components/BaseLink";
import PatientReportsTable from "../../components/PatientReportsTable";
import PatientReportsList from "../../components/PatientReportsList";
import InternalProviderNotes from "../../components/InternalProviderNotes";
import PatientNotes from "../../components/PatientNotes";
import ConciergeItemSkeleton from "../../components-skeleton/ConciergeItemSkeleton";
import DetailFellowSwitch from "../../components/DetailFellowSwitch";
import DetailPaymentSwitch from "../../components/DetailPaymentSwitch";
import { UserRoleViewPermissions, ViewPermissions } from "../../constants";
import { useMutation, useLazyQuery } from '@apollo/client';
import { 
  ReportLock, ReportUnLock, PaitentMeetingCreate, 
  PaitentMeetingDelete, GetChangeLogData, 
  SetPatientMeetingPaid } from "../../apollo-client/api-query";
import { capitalizeFirstLetter } from "../../hooks/use-capitalize-first-letter";
import { openModal, refetchMe, refetchPatient } from "../../store";
import { formatPhoneNumber } from "../../hooks/use-format-phone-number";
import { findMemberByUserId } from "../../utils/member-lookup";

const PatientDetail = () => {
  const [changeLogData, setChangeLogData] = useState([])
  const dispatch = useDispatch()
  const { id } = useParams()
  const [getChangeLogData, { loading: loadingFetch }] = useLazyQuery(GetChangeLogData, {
    fetchPolicy: 'no-cache',
  });

  const reports = useSelector(state => state.patientReports)
  const loadingGetReports = useSelector(state => state.isLoadingPatientReports)
  const auth = useSelector(state => state.auth)
  const loadingAuth = useSelector(state => state.loadingAuth)
  // const isStaffPhysician = useSelector(state => state.isStaffPhysician)
  const members = useSelector(state => state.members)
  const selectedPatient = useMemo(() => findMemberByUserId(members, id), [members, id])
  const myViewPermissions = useMemo(() => {
    const result = [];
    const myRolePermissions = auth?.roles?.reduce((acc, role) => {
      const rolePermissions = UserRoleViewPermissions[role] || [];
      rolePermissions.forEach(permission => {
        if (!acc.includes(permission)) {
          acc.push(permission);
        }
      });
      return acc;
    }, []) || [];

    result.push(...(myRolePermissions || []));
    if (auth?.id && selectedPatient?.user?.assignedStaff) {
      selectedPatient.user.assignedStaff.forEach(el => {
        if (el.staffId === auth.id && Array.isArray(UserRoleViewPermissions[el.role])) {
          result.push(...UserRoleViewPermissions[el.role]);
        }
      });
    }

    return result;
  }, [auth, selectedPatient])
  const [isMyPatient, setIsMyPatient] = useState(false);
  const [lockReport] = useMutation(ReportLock)
  const [unlockReport] = useMutation(ReportUnLock)
  const [openSortNav] = useState(false)
  const [displayedData, setDisplayedData] = useState([]);

  const [isPaitentMeeting, setIsPaitentMeeting] = useState(null);
  const [isPaitentMeetingPaid, setIsPaitentMeetingPaid] = useState(false);
  const [paitentMeetingCreate] = useMutation(PaitentMeetingCreate)
  const [paitentMeetingDelete] = useMutation(PaitentMeetingDelete)
  const [setPatientMeetingPaid] = useMutation(SetPatientMeetingPaid)

  const fetchData = () => {
    dispatch(refetchPatient(true))
  }

  useEffect(() => {
    dispatch(refetchMe(true));
  }, [isPaitentMeeting, isPaitentMeetingPaid]);

  const fetchLogData = useCallback(async (id) => {
    if (!loadingFetch) {
      const result = await getChangeLogData({
        variables: {
          id
        }
      });
      if (result?.data?.node?.patientChangeLogs && result?.data?.node?.patientChangeLogs?.edges.length > 0) {
        setChangeLogData([...(result?.data?.node?.patientChangeLogs?.edges || [])].sort(
          (a, b) => moment(b.node.createdAt) - moment(a.node.createdAt)
        ));
      }
    }
  }, [getChangeLogData, loadingFetch]);

  useEffect(() => {
    if (Array.isArray(selectedPatient?.user?.assignedStaff)) {
      selectedPatient?.user?.assignedStaff.forEach(el => {
        if (el.staff.name === auth?.name) {
          setIsMyPatient(true);
        }
      })
    }
    if (selectedPatient?.user?.firstMeeting?.id !== undefined) {
      setIsPaitentMeeting(selectedPatient?.user?.firstMeeting?.id)
      setIsPaitentMeetingPaid(selectedPatient?.user?.firstMeeting?.paidAt)
    }
    else {
      setIsPaitentMeeting(null);
    };
    if(selectedPatient?.user?.id && myViewPermissions.includes(ViewPermissions.READ_PATIENT_CHANGE_LOG)){
      fetchLogData(selectedPatient?.user?.id);
    }
  }, [selectedPatient, isMyPatient])

  useEffect(() => {
    setDisplayedData(changeLogData.slice(0, 3));
  }, [changeLogData])

  useEffect(() => {

  }, []);

  const handleShowAll = () => {
    dispatch(
      openModal({
        currentModal: 'CHANGELOG_MODAL',
        modalProps: {
          changeLogData: changeLogData,
          parentWidthStyle: {
            xs: '100%', sm: '100%'
          }
        }
      }))
  }
  const moveToReportSection = () => {
    document.querySelector('#report-container').scrollIntoView({ behavior: "smooth" });
  }

  const handleClickFirstMeeting = async () => {
    if (isPaitentMeeting === null) {
      const { data } = await paitentMeetingCreate({
        variables: {
          memberId: id,
          staffId: auth?.id
        }
      });
      setIsPaitentMeeting(data.patientMeetingCreate.id);
      setIsPaitentMeetingPaid(!!data.patientMeetingCreate.paidAt)
    } else {
      try {
        const { data } = await paitentMeetingDelete({
          variables: {
            id: isPaitentMeeting
          }
        });
        if (data.delete === true)
        {
          setIsPaitentMeeting(null);
          setIsPaitentMeetingPaid(false)
        }
      } catch (error) {
        
      }
    }
  };

  const handleClickEditProfile = () => {
    dispatch(
      openModal({
        currentModal: 'EDIT_PATIENT_DETAIL_MODAL',
      }))
  }

  const handleClickPaitentMeetingPaid = async () => {
    if(isPaitentMeeting) {
      const isPaid = !isPaitentMeetingPaid;
      await setPatientMeetingPaid({
        variables: {
          meetingId: isPaitentMeeting,
          paid: isPaid
        }
      })
      setIsPaitentMeetingPaid(!isPaitentMeetingPaid)
    }
  }
  
  return (
    <MainContainer>
      <BlockContainer
        sx={{
          position: 'relative'
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: { xs: '24px 16px', md: '32px 16px 0' },
            borderRadius: { md: '16px' },
            boxShadow: '0px 2px 10px 0px #5B87EF1A',
            bgcolor: 'white'
          }}
        >
          <Stack spacing={'40px'}>
            <Box>
              <BaseLink
                to='/'
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ArrowBackIcon
                  sx={{
                    color: 'primary.mediumGray',
                    mr: '10px'
                  }}
                />
                <Typography variant="body" color="primary.darkGray">
                  Back to Patient List
                </Typography>
              </BaseLink>
            </Box>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: {
                    xs: '4px',
                    md: 2
                  },
                  alignItems: {
                    xs: 'flex-start',
                    md: 'center'
                  },
                  flexDirection: {
                    xs: 'column',
                    md: 'row'
                  }
                }}
              >
                <Typography
                  variant="h1"
                  color='primary.black'
                >
                  Patient details
                </Typography>
                {myViewPermissions.includes(ViewPermissions.VIEW_REPORT) && (
                  <Typography
                    onClick={moveToReportSection}
                    variant="body"
                    color='primary.mainBlue'
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      mt: 0
                    }}
                  >Go to reports</Typography>
                )}
              </Box>
            </Box>
            {myViewPermissions.includes(ViewPermissions.READ_PATIENT_CHANGE_LOG) && (
              <Box 
                  sx={{
                    mt: '24px',
                    'box-shadow': '0px 2px 10px 0px #5B87EF33',
                    padding: '14px 16px',
                    borderRadius: '8px'
                  }}
                >
                <Typography
                  variant="h3"
                  color='primary.black'
                  style={{lineHeight: '28px'}}
                >
                  Change Log
                </Typography>
                <Grid container sx={{ borderBottom: '1px solid #ABB7C0', paddingBottom: '4px', flexWrap: 'nowrap' }}>
                  <Grid item xs={2} sx={{minWidth: '60px'}}>
                    <Typography variant="h4">Date</Typography>
                  </Grid>
                  <Grid item xs={2} sx={{minWidth: '80px'}}>
                    <Typography variant="h4">Time</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="h4">Change</Typography>
                  </Grid>
                </Grid>

                {/* Data Row */}
                {displayedData.map(item => (
                  <Grid container sx={{paddingTop: '4px', flexWrap: 'nowrap'}}>
                    <Grid item xs={2} sx={{minWidth: '60px'}}>
                      <Typography variant="body">{ moment(item.node.createdAt).format('YYYY-MM-DD') }</Typography>
                    </Grid>
                    <Grid item xs={2} sx={{minWidth: '80px'}}>
                      <Typography variant="body">{ moment(item.node.createdAt).format('HH:mm:ss') }</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body">{ item.node.change }</Typography>
                    </Grid>
                  </Grid>                  
                ))}
                {changeLogData.length > 3 && (
                  <Box sx={{mt: '13px', display: 'flex', justifyContent: 'center'}}>
                    <button style={{
                        'backgroundColor': '#C6E7FF',
                        color: '#4266B8',
                        height: '20px' ,
                        width: '82px',
                        padding: 0,
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '4px',
                        outline: 0,
                        border: 'none'
                      }}
                      onClick={handleShowAll}
                    >
                      Show more
                    </button>
                  </Box>
                )}
                {changeLogData.length === 0 && (
                  <Typography variant="body">Patient has no change log data</Typography>
                )}
              </Box>
            )}
            <Box>
            {myViewPermissions.includes(ViewPermissions.READ_INTERNAL_NOTE) && 
              <InternalProviderNotes
                sx={{
                  mt: '16px'
                }}
              />
            }
            {myViewPermissions.includes(ViewPermissions.READ_PUBLIC_NOTE) && 
              <PatientNotes
                sx={{
                  mt: '16px'
                }}
              />
            }
            </Box>
            <Box>
              <Stack spacing={{ xs: '24px', md: '16px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    variant="h2"
                    color="primary.darkGray"
                  >
                    Demographics
                  </Typography>
                  {myViewPermissions.includes(ViewPermissions.EDIT_PATIENT) && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onClick={handleClickEditProfile}
                    >
                      <Typography
                        variant="body"
                        color='primary.mainBlue'
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          mt: 0
                        }}
                      >
                        Edit
                      </Typography>
                      <BaseImage
                        sx={{
                          width: '16px',
                          height: '16px',
                          display: 'flex'
                        }}
                        sximage={{ display: 'flex' }}
                        src={EditIcon}
                        alt="icon-edit"
                      />
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' }
                  }}
                >
                  {loadingAuth ? (
                    <Skeleton
                      variant="circular"
                      sx={{
                        minWidth: '80px',
                        width: '80px',
                        height: '80px',
                      }}
                    />
                  ) : (
                    <BaseImage
                      sx={{
                        minWidth: '80px',
                        width: '80px',
                        height: '80px',
                      }}
                      sximage={{
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      src={selectedPatient?.user?.avatarUrl || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
                      alt='profile-avatar'
                    />
                  )}
                  
                  <Box
                    sx={{
                      width: '100%',
                      flexGrow: 1,
                      ml: { md: '16px' },
                      mt: { xs: '16px', md: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      {loadingAuth ? (
                        <Stack direction={'column'} spacing={1}>
                          <Skeleton variant="rounded" width={'100%'} height={25} />
                          <Skeleton variant="rounded" width={'100%'} height={25} />
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            width: '100%',
                            height: '100%',
                            gap: '10px'
                          }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: '48px',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Typography variant="h3">
                              {selectedPatient?.user?.name}
                            </Typography>
                            <Typography variant="body" color='primary.darkGray' sx={{ textDecoration: 'underline' }}>
                              {selectedPatient?.user?.email || 'accountname@email.com'}
                            </Typography>
                          </Box>
                          
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              gap: '10px',
                            }}
                          >
                            <Typography variant="h4" whiteSpace={'nowrap'}>
                              Had first meeting with Fellow?
                            </Typography>
                            <Box
                              sx={{
                                width: '100%',
                                height: '1px',
                                bgcolor: 'primary.lightGray',
                                mx: '8px'
                              }}
                            />
                            
                            {myViewPermissions.includes(ViewPermissions.EDIT_FIRST_MEETING) ? (
                              <DetailFellowSwitch
                                needsReview={isPaitentMeeting === null ? false : true}
                                onClick={handleClickFirstMeeting}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '50px',
                                  gap: '10px',
                                  flexShrink: '0',
                                }}
                              >
                                <BaseImage
                                  sx={{
                                    width: '12px',
                                    height: '12px',
                                    display: 'flex',
                                    flexShrink: '0',
                                  }}
                                  sximage={{ display: 'flex' }}
                                  src={isPaitentMeeting ? YesIcon : NoIcon}
                                  alt="icon-yes"
                                />
                                <Typography variant="body" whiteSpace={'nowrap'} color='primary.darkGray'>
                                  {isPaitentMeeting ? "Yes" : "No"}
                                </Typography>
                              </Box>
                            )}
                           
                          </Box>
                          {myViewPermissions.includes(ViewPermissions.READ_PAID) && 
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: '10px',
                              }}
                            >
                              <Typography variant="h4" whiteSpace={'nowrap'}>
                                Payment received?
                              </Typography>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '1px',
                                  bgcolor: 'primary.lightGray',
                                  mx: '8px'
                                }}
                              />
                              {(isPaitentMeeting && myViewPermissions.includes(ViewPermissions.EDIT_PAID)) ? (
                                <DetailPaymentSwitch 
                                  needsReview={!isPaitentMeetingPaid}
                                  onClick={handleClickPaitentMeetingPaid}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '50px',
                                    flexShrink: '0',
                                  }}
                                >
                                  {
                                    <BaseImage
                                      sx={{
                                        width: '12px',
                                        height: '12px',
                                        display: 'flex',
                                        flexShrink: '0',
                                      }}
                                      sximage={{ display: 'flex' }}
                                      src={isPaitentMeetingPaid ? YesIcon : NoIcon}
                                      alt="icon-no"
                                    />
                                  }
                                  <Typography variant="body" whiteSpace={'nowrap'} color='primary.darkGray'>
                                    {isPaitentMeetingPaid ? "Yes" : "No"}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                          {/*  
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}
                            >
                              <Typography variant="h4" whiteSpace={'nowrap'}>
                                Send report notification email?
                              </Typography>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '1px',
                                  bgcolor: 'primary.lightGray',
                                  mx: '8px'
                                }}
                              />                           
                                {myViewPermissions.includes(ViewPermissions.TOGGLE_EMAILS) ? (
                                  <DetailSendEmailSwitch />
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '50px',
                                      gap: '10px',
                                      flexShrink: '0',
                                    }}
                                  >
                                    <BaseImage
                                      sx={{
                                        width: '12px',
                                        height: '12px',
                                        display: 'flex',
                                        flexShrink: '0',
                                      }}
                                      sximage={{ display: 'flex' }}
                                      src={NoIcon}
                                      alt="icon-no"
                                    />
                                    <Typography variant="body" whiteSpace={'nowrap'} color='primary.darkGray'>
                                      No
                                    </Typography>
                                  </Box>
                                )} 
                            </Box>
                          */}
                        </Box>
                      )}
                    </Box>
                  </Box> 
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      width: '100%',
                      height: '100%',
                      gap: '16px',
                      ml: { md: '16px' },
                      mt: { xs: '16px', md: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          backgroundColor: 'primary.lightGreen1',
                          padding: '13px 12px 12px',
                          borderRadius: '5px',
                          height: '48px'
                        }}
                      >
                        {loadingAuth ? (
                          <Skeleton variant="rounded" width={'100%'} height={25} />
                        ) : (
                          <Typography variant="body">
                            Sex assigned at birth: <strong>{capitalizeFirstLetter(selectedPatient?.user?.sex) || 'Female'}</strong>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      {loadingAuth ? (
                        <Skeleton variant="rounded" width={'100%'} height={25} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="h4" whiteSpace={'nowrap'}>
                            Date of birth
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              height: '1px',
                              bgcolor: 'primary.lightGray'
                            }}
                          />
                          <Typography variant="body" whiteSpace={'nowrap'}>
                            {selectedPatient?.user?.dateOfBirth ? moment(selectedPatient?.user?.dateOfBirth).format('MM/DD/YYYY') : 'XX/XX/XXXX'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      {loadingAuth ? (
                        <Skeleton variant="rounded" width={'100%'} height={25} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              whiteSpace: 'nowrap'
                            }}
                          >
                            #KIT CODE
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              height: '1px',
                              bgcolor: 'primary.lightGray'
                            }}
                          />
                          <Typography variant="body" color='primary.darkGray'>
                            {selectedPatient?.user?.kits?.[0]?.accessionId}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      {loadingAuth ? (
                        <Skeleton variant="rounded" width={'100%'} height={25} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="h4" whiteSpace={'nowrap'}>
                            Phone number
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              height: '1px',
                              bgcolor: 'primary.lightGray',
                              mx: '8px'
                            }}
                          />
                          <Typography variant="body" whiteSpace={'nowrap'} color='primary.darkGray'>
                            {formatPhoneNumber(selectedPatient?.user?.phone) || '(XXX) XXX-XXXX'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Kit Status report */}
                {/* <KitStatusReport
                  statusData={selectedPatient?.user?.kitStatusReports}
                  memberId={selectedPatient?.user?.id}
                  kitCode={selectedPatient?.user?.kits?.[0]?.accessionId}
                  roles={auth?.roles}
                /> */}
                
                {myViewPermissions.includes(ViewPermissions.READ_PATIENT_MEDICATIONS) && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: { md: '16px' },
                      border: '1px solid #f6f9fc',
                      borderRadius: '8px'
                    }}
                  >
                    <Typography
                      variant="h3"
                      color="primary.darkGray"
                    >
                      Current medication
                    </Typography>
                    <Box
                      sx={{
                        borderTop: '1px solid #ABB7C0',
                        pt: '16px',
                        mt: '16px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                        }}
                      >
                        {loadingAuth && (
                          <Stack direction={'row'} spacing={1}>
                            <Skeleton variant="rounded" width={80} height={40} />
                            <Skeleton variant="rounded" width={80} height={40} />
                            <Skeleton variant="rounded" width={80} height={40} />
                          </Stack>
                        )}
                        {selectedPatient?.user?.medications && selectedPatient?.user?.medications.map((medication => (
                          <Box
                            key={medication.name}
                            sx={{
                              mr: '8px',
                              borderRadius: '5px',
                              padding: '12px',
                              bgcolor: 'primary.lightBlue',
                              mb: { xs: '4px', md: '8px' }
                            }}
                          >
                            <Typography variant="body">{medication.name}</Typography>
                          </Box>
                        )))}
                        {(!selectedPatient?.user?.medications || selectedPatient?.user?.medications.length === 0) && !loadingAuth && (
                          <Typography variant="body">Patient has no current medication</Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: { md: '16px' },
                    border: '1px solid #f6f9fc',
                    borderRadius: '8px'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      variant="h3"
                      color="primary.darkGray"
                    >
                      Staff assigned
                    </Typography>
                    {myViewPermissions.includes(ViewPermissions.ASSIGN_STAFF) && (
                      <BaseLink to={`/patient/${id}/edit-staff`}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                          <Typography
                            variant="body"
                            color='primary.mainBlue'
                            sx={{
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              mt: 0
                            }}
                          >
                            Edit Staff
                          </Typography>
                          <BaseImage
                            sx={{
                              width: '16px',
                              height: '16px',
                              display: 'flex'
                            }}
                            sximage={{ display: 'flex' }}
                            src={EditIcon}
                            alt="icon-edit"
                          />
                        </Box>
                      </BaseLink>
                    )}
                  </Box>
                  <Box
                    sx={{
                      borderTop: '1px solid #ABB7C0',
                      mt: '16px',
                    }}
                  >
                    {loadingAuth && (
                      <Grid container spacing={'16px'}>
                        {new Array(3).fill(null).map(((_, idx) => (
                          <Grid item xs={12} md={4} key={idx}>
                            <ConciergeItemSkeleton />
                          </Grid>
                        )))}
                      </Grid>
                    )}
                    <Grid container spacing={'16px'}>
                      {selectedPatient?.user?.assignedStaff && selectedPatient?.user?.assignedStaff.map(((item, idx) => (
                        <Grid item xs={12} md={4} key={idx} display={'flex'}>
                          <ConciergeItem
                            item={item}
                          />
                        </Grid>
                      )))}
                    </Grid>
                  </Box>
                  {!loadingAuth && (!selectedPatient?.user?.assignedStaff || selectedPatient?.user?.assignedStaff.length === 0) && (
                    <Typography variant="body" mt="16px">Patient has no Staff assigned</Typography>
                  )}
                </Box>
              </Stack>
            </Box>
            {myViewPermissions.includes(ViewPermissions.VIEW_REPORT) && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  id="report-container"
                >
                  <Typography
                    variant="h2"
                    color="primary.black"
                  >
                    Patient reports ({reports?.length})
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      padding: '10px 0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'none' }
                      }}
                    >
                      <MedicationSortDropdown setOpenNav={() => { }} />
                      {openSortNav && (
                        <MedicationSortMobileNavbar
                          isOpen={openSortNav}
                        // setIsOpen={setOpenSortNav}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    <PatientReportsTable
                      reports={reports}
                      refetchReports={fetchData}
                      loadingGetReports={loadingGetReports}
                      isMyPatient={isMyPatient}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'block', md: 'none' }
                    }}
                  >
                    <PatientReportsList
                      reports={reports}
                      lockReport={lockReport}
                      unlockReport={unlockReport}
                      refetchReports={fetchData}
                      loadingGetReports={loadingGetReports}
                      isMyPatient={isMyPatient}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </BlockContainer>
    </MainContainer>
  );
};

export default PatientDetail;
